import { MetricType } from '@strength/domain';
import {
  RepMaxBenchmarkData,
  InputDisplay,
  WorkoutExerciseMetric,
  WorkoutPublishErrors,
  WorkoutSet,
} from '@strength/domain-workout';
import { tZodIssue } from '@strength/i18n';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CollectionFlag } from '../../../components/icons/collection-flag';
import { RenderCellContentProps } from '../../../components/sheet/slots/metrics-slot';
import { useMetrics } from '../../../hooks/useMetrics';
import { ErrorContentWrapper } from '../../block/slots/error-content-wrapper';

type Context = {
  workoutErrors: WorkoutPublishErrors | null;
  applyBenchmarkData: RepMaxBenchmarkData | undefined;
  libraryExerciseId: string;
};

export const ActualSetMetricCellContent = ({
  row,
  metric,
  context,
  hideCollectionFlag,
  disabled,
  badge,
  originalSet,
  inputDisplay,
}: RenderCellContentProps<
  WorkoutSet,
  WorkoutExerciseMetric,
  MetricType,
  Context
> & {
  inputDisplay: InputDisplay;
}) => {
  const className = 'flex items-center w-full h-full py-2 pl-2';

  const { getMetric } = useMetrics();
  const { t } = useTranslation();

  const prescribedMetric = useMemo(() => {
    return originalSet?.metrics.find((m) => m.metricId === metric.metricId);
  }, [originalSet, metric.metricId]);

  const actualMetric = useMemo(() => {
    return row.metrics.find((m) => m.metricId === metric.metricId);
  }, [row.metrics, metric.metricId]);

  const showCollectionFlag = !!(
    !hideCollectionFlag && actualMetric?.forCollection
  );

  const { workoutErrors } = context;

  const { value, unit, actualParseError } = inputDisplay;

  let finalUnit = unit;
  if ((typeof value === 'string' && !value.length) || !unit) {
    finalUnit = '';
  }
  const errorsForCell =
    workoutErrors?.get(row.workoutSetId)?.map((err) => t(...err)) || [];
  const workoutHasErrors = errorsForCell.length > 0;

  if (actualParseError) {
    const showTooltip =
      workoutHasErrors || String(actualMetric?.input || '').length > 0;
    const errors = actualParseError.issues
      .map((issue) => tZodIssue(issue, t))
      .concat(errorsForCell);
    return (
      <ErrorContentWrapper
        className={className}
        useTooltip={showTooltip}
        disabled={disabled}
        errors={errors}
        showCollectionFlag={showCollectionFlag}
        displayValue={actualMetric?.input}
      />
    );
  }

  if (workoutHasErrors) {
    return (
      <ErrorContentWrapper
        className={className}
        useTooltip={true}
        disabled={disabled}
        errors={errorsForCell}
        showCollectionFlag={showCollectionFlag}
        displayValue={`${value}${finalUnit}`}
      />
    );
  }

  return (
    <div className={className}>
      {`${value}${finalUnit}`}
      {showCollectionFlag && <CollectionFlag />}
      {badge && badge}
    </div>
  );
};
